import React, { useEffect, useMemo, useState } from "react";
import { FaEnvelope, FaSave, FaTimes, FaUser } from "react-icons/fa";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const DEFAULT_FORM = {
  nom: "",
  prenom: "",
  email: "",
};

function getPreviewInitials(formData) {
  const nom = formData?.nom?.[0] || "";
  const prenom = formData?.prenom?.[0] || "";
  return `${prenom}${nom}`.toUpperCase() || "EN";
}

const TeacherModal = ({
  isOpen,
  onClose,
  onSave,
  demoMode = false,
}) => {
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    setFormData(DEFAULT_FORM);
    setSaving(false);
    setErrorMessage("");
  }, [isOpen]);

  const modalTitle = useMemo(() => {
    return "Ajouter un enseignant";
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (demoMode) {
      setErrorMessage(
        "Mode Démo : les modifications ne peuvent pas être enregistrées."
      );
      return;
    }

    if (
      !formData.nom.trim() ||
      !formData.prenom.trim() ||
      !formData.email.trim()
    ) {
      setErrorMessage("Le nom, le prénom et l'email sont obligatoires.");
      return;
    }

    try {
      setSaving(true);

      const success = await onSave({
        nom: formData.nom.trim(),
        prenom: formData.prenom.trim(),
        email: formData.email.trim(),
      });

      if (success) {
        setFormData(DEFAULT_FORM);
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120] bg-slate-950/70 backdrop-blur-sm">
      <div className="flex h-[50dvh] items-stretch justify-center p-0 sm:p-4 pt-6">
        <div className="flex h-[50dvh] w-full max-w-3xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900 sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:rounded-[28px] sm:border sm:border-white/10 dark:sm:border-slate-700">
          {/* Header */}
          <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-4 dark:border-slate-800 dark:bg-slate-900 sm:px-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                  Enseignant
                </p>
                <h2 className="mt-1 text-xl font-black text-slate-900 dark:text-slate-100 sm:text-2xl">
                  {modalTitle}
                </h2>
              </div>

              <button
                onClick={onClose}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="grid min-h-full grid-cols-1 lg:grid-cols-[0.9fr_1.1fr]">
              {/* Preview */}
              <div className="border-b border-white/10 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 p-5 text-white lg:border-b-0 lg:border-r lg:border-white/10 sm:p-6">
                <p className="inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                  Aperçu
                </p>

                <div className="mt-6 flex flex-col items-center rounded-[20px] border border-white/10 bg-white/10 p-4 text-center">
                  <div className="flex h-24 w-24 items-center justify-center rounded-full bg-white/10 text-white/70 shadow-xl sm:h-28 sm:w-28">
                    <span className="text-2xl font-black sm:text-3xl">
                      {getPreviewInitials(formData)}
                    </span>
                  </div>

                  <h3 className="mt-4 text-lg font-bold sm:text-xl">
                    {formData.prenom || "Prénom"} {formData.nom || "Nom"}
                  </h3>

                  <p className="mt-2 text-sm text-white/75">
                    {formData.email || "Email"}
                  </p>
                </div>
              </div>

              {/* Form */}
              <div className="p-4 sm:p-6">
                {errorMessage && (
                  <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                    {errorMessage}
                  </div>
                )}

                <form id="teacher-form" onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                        Nom
                      </label>
                      <div className="relative">
                        <FaUser className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          name="nom"
                          value={formData.nom}
                          onChange={handleChange}
                          className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                        Prénom
                      </label>
                      <div className="relative">
                        <FaUser className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          name="prenom"
                          value={formData.prenom}
                          onChange={handleChange}
                          className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                      Email
                    </label>
                    <div className="relative">
                      <FaEnvelope className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                        required
                      />
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="shrink-0 border-t border-slate-200 bg-white px-4 py-4 dark:border-slate-800 dark:bg-slate-900 sm:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-800 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                form="teacher-form"
                disabled={demoMode || saving}
                className={cn(
                  "inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 font-semibold text-white transition",
                  demoMode || saving
                    ? "cursor-not-allowed bg-slate-400 dark:bg-slate-700"
                    : "bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                )}
              >
                <FaSave />
                {saving ? "Enregistrement..." : "Ajouter"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherModal;